import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { STATIONS, DIVISIONS, DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

export default function Stations() {
  const [division, setDivision] = useState("openM");

  return (
    <div data-testid="stations-page" className="pt-16">
      <Seo
        title="HYROX Format & 8 Stations Explained | HyroxNinja"
        description="The full HYROX race format: 8 × 1km runs and all 8 stations — SkiErg, Sled Push, Sled Pull, Burpee Broad Jump, Row, Farmers Carry, Sandbag Lunges, Wall Balls — with official Open and Pro weights."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.stations} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
          <p className="eyebrow mb-4">The Official Format</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-8">
          <MaskedLine delay={0.1}>The Anatomy of</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">a HYROX Race</MaskedLine>
        </h1>
        <p className="text-zinc-400 max-w-2xl leading-relaxed">
          Every HYROX race is identical worldwide: you run 1km, you hit a
          station, and you repeat that 8 times. No surprises, no random WODs —
          just a fixed course you can train for with surgical precision. That's
          exactly what we train at Hyrox Ninja.
        </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-8">
        <Reveal>
          <div className="flex flex-wrap items-center gap-2 border border-white/10 p-2 w-fit bg-[#0c0c0c]" data-testid="division-selector">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 px-2">Division:</span>
            {DIVISIONS.map((d) => (
              <button
                key={d.key}
                onClick={() => setDivision(d.key)}
                data-testid={`division-tab-${d.key}`}
                className={`font-head uppercase text-sm font-bold px-4 py-2 transition-colors ${
                  division === d.key ? "bg-volt text-black" : "text-zinc-300 hover:text-white"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        {STATIONS.map((s, i) => (
          <Reveal key={s.n} delay={Math.min(i * 0.04, 0.2)}>
            <div className="station-row grid md:grid-cols-12 gap-4 md:gap-6 py-8 items-center" data-testid={`station-card-${s.n}`}>
              <div className="md:col-span-1">
                <p className="font-head font-black text-4xl lg:text-5xl text-outline-volt leading-none">{s.n}</p>
              </div>
              <div className="md:col-span-3">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Run 1km, then —</p>
                <h3 className="font-head font-extrabold uppercase text-2xl lg:text-3xl tracking-tight">{s.name}</h3>
                <p className="font-mono2 text-xs text-volt mt-1 uppercase tracking-wider">{s.metric}</p>
              </div>
              <div className="md:col-span-4">
                <p className="text-zinc-400 text-sm leading-relaxed">{s.details}</p>
              </div>
              <div className="md:col-span-2">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                  {DIVISIONS.find((d) => d.key === division).label}
                </p>
                <p className="font-head font-bold uppercase text-lg text-white" data-testid={`station-weight-${s.n}`}>
                  {s.weights[division]}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Targets</p>
                <p className="text-zinc-300 text-sm">{s.focus}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <Reveal>
          <div className="border border-white/10 bg-[#0c0c0c] p-7 flex flex-wrap items-center justify-between gap-4" data-testid="stations-gear-callout">
            <div>
              <p className="eyebrow mb-2">Train It at Home</p>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-xl">
                Sleds, sandbags, kettlebells, wall balls — build your own station setup with
                race-standard Centr gear and the official HYROX bundles.
              </p>
            </div>
            <Link to="/gear" className="btn-ghost shrink-0" data-testid="stations-gear-link">
              Race-Standard Gear <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <Reveal>
          <p className="text-zinc-500 text-xs leading-relaxed mt-6" data-testid="stations-blog-link">
            See how all 8 stations stack together on sim day:{" "}
            <Link to="/blog/hyrox-simulation-workout" className="text-volt hover:text-white transition-colors underline underline-offset-2">the simulation workout, station by station</Link>.
          </p>
        </Reveal>
      </section>

      <section className="bg-volt text-black py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-head font-black uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Train every station with a coach.
          </h2>
          <Link to="/schedule#book-now" data-testid="stations-book-button" className="inline-flex items-center gap-2 bg-black text-volt font-head font-bold uppercase tracking-wide px-8 py-4 text-lg hover:scale-105 transition-transform" onClick={() => trackEvent("booking_click", { placement: "stations_page", service: "general" })}>
            Book a Session <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
